import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CreditCard } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

function CheckoutPage({ cart, clearCart, user, setUser }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    kennitala: ''
  });

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        customer_kennitala: formData.kennitala,
        total_amount: total
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Save user info
        setUser({ name: formData.name, email: formData.email, phone: formData.phone });
        localStorage.setItem('user', JSON.stringify({ name: formData.name, email: formData.email, phone: formData.phone }));
        
        // Redirect to mock payment
        window.location.href = result.payment_url;
      } else {
        throw new Error('Order creation failed');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast({
        title: "Villa kom upp",
        description: "Ekki tókst að stofna pöntun",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl md:text-5xl font-bold text-[#2C3E50] mb-8" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          Greiðsla
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Customer Info Form */}
          <Card className="border-[#E8ECEF]">
            <CardHeader>
              <CardTitle className="text-2xl text-[#2C3E50]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Upplýsingar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-[#2C3E50]">Nafn *</Label>
                  <Input 
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="mt-2"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#2C3E50]">Netfang *</Label>
                  <Input 
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="mt-2"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#2C3E50]">Sími *</Label>
                  <Input 
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-2"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="kennitala" className="text-[#2C3E50]">Kennitala *</Label>
                  <Input 
                    id="kennitala"
                    value={formData.kennitala}
                    onChange={(e) => setFormData({...formData, kennitala: e.target.value})}
                    placeholder="000000-0000"
                    className="mt-2"
                    required
                  />
                </div>

                <div className="pt-4">
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-[#0A4D8C] hover:bg-[#083A6B] text-white py-6 text-lg rounded-lg"
                    data-testid="complete-order-button"
                  >
                    <CreditCard className="mr-2 h-5 w-5" />
                    {loading ? 'Vinnsla...' : 'Halda áfram í greiðslu'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Order Summary */}
          <Card className="border-[#E8ECEF]">
            <CardHeader>
              <CardTitle className="text-2xl text-[#2C3E50]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Yfirlit Pöntunar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {cart.map((item) => (
                  <div key={item.id} className="flex justify-between items-center py-2 border-b border-[#E8ECEF]">
                    <div>
                      <p className="font-medium text-[#2C3E50]">{item.name}</p>
                      <p className="text-sm text-[#8B9BA8]">Magn: {item.quantity}</p>
                    </div>
                    <p className="font-semibold text-[#0A4D8C]">
                      {(item.price * item.quantity).toLocaleString()} kr
                    </p>
                  </div>
                ))}

                <div className="pt-4 border-t-2 border-[#E8ECEF]">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-lg text-[#8B9BA8]">Millisamtals:</span>
                    <span className="text-lg font-semibold text-[#2C3E50]">{total.toLocaleString()} kr</span>
                  </div>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg text-[#8B9BA8]">VSK (24%):</span>
                    <span className="text-lg font-semibold text-[#2C3E50]">{Math.round(total * 0.24).toLocaleString()} kr</span>
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t-2 border-[#0A4D8C]">
                    <span className="text-2xl font-bold text-[#2C3E50]">Heildarverð:</span>
                    <span className="text-3xl font-bold text-[#0A4D8C]">{Math.round(total * 1.24).toLocaleString()} kr</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
